import React, { useEffect, useRef, useState } from 'react';
import WaveformPlaylist from 'waveform-playlist';

const MultitrackPlayer = ({ tracks, modelConfig, selectedExample, getStemIcon, generationTime, hideHeader = false }) => {
  console.log(`🕒 MultitrackPlayer: generationTime for ${modelConfig.id}:`, generationTime);
  const containerRef = useRef(null);
  const playlistRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [validatedTracks, setValidatedTracks] = useState(null);
  const [trackStates, setTrackStates] = useState({}); // Track solo/mute states
  const [isPlaying, setIsPlaying] = useState(false); // Track play/pause state
  const unhandledRejectionHandlerRef = useRef(null);

  const handlePlay = () => {
    if (playlistRef.current) {
      playlistRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (playlistRef.current) {
      playlistRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handlePlayPauseToggle = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const handleStop = () => {
    if (playlistRef.current) {
      playlistRef.current.stop();
      setIsPlaying(false);
      // After stopping, reset any solo/mute state so playback restarts cleanly
      setTimeout(() => resetAllMuteSoloStates(), 50);
    }
  };

  // Function to mute/unmute a specific track
  const muteTrack = (trackIndex, shouldMute) => {
    if (!playlistRef.current) {
      console.error('No playlist reference available');
      return;
    }
    
    console.log(`🔊 Attempting to ${shouldMute ? 'MUTE' : 'UNMUTE'} track ${trackIndex}`);
    
    try {
      const playlist = playlistRef.current;
      let success = false;
      
      // Method 1: Try direct playlist tracks access
      if (playlist.tracks && playlist.tracks[trackIndex]) {
        const originalGain = playlist.tracks[trackIndex].gain;
        playlist.tracks[trackIndex].gain = shouldMute ? 0 : 1;
        console.log(`✅ Track ${trackIndex} gain changed from ${originalGain} to ${playlist.tracks[trackIndex].gain} via tracks array`);
        success = true;
      }
      
      // Method 2: Try getInfo API
      if (!success && playlist.getInfo) {
        try {
          const info = playlist.getInfo();
          if (info.tracks && info.tracks[trackIndex]) {
            const originalGain = info.tracks[trackIndex].gain;
            info.tracks[trackIndex].gain = shouldMute ? 0 : 1;
            console.log(`✅ Track ${trackIndex} gain changed from ${originalGain} to ${info.tracks[trackIndex].gain} via getInfo`);
            success = true;
          }
        } catch (e) {
          console.log('getInfo method failed:', e);
        }
      }
      
      // Method 3: Try event emitter approach
      if (!success && playlist.ee) {
        try {
          if (shouldMute) {
            playlist.ee.emit('mute', trackIndex);
          } else {
            playlist.ee.emit('unmute', trackIndex);
          }
          console.log(`✅ Track ${trackIndex} ${shouldMute ? 'muted' : 'unmuted'} via event emitter`);
          success = true;
        } catch (e) {
          console.log('Event emitter method failed:', e);
        }
      }
      
      // Method 4: Fallback to direct audio element control
      if (!success) {
        const container = containerRef.current;
        const channels = container?.querySelectorAll('.channel');
        if (channels && channels[trackIndex]) {
          const audioElements = channels[trackIndex].querySelectorAll('audio');
          audioElements.forEach(audio => {
            const originalVolume = audio.volume;
            audio.volume = shouldMute ? 0 : 1;
            console.log(`✅ Audio element volume changed from ${originalVolume} to ${audio.volume}`);
          });
          if (audioElements.length > 0) {
            console.log(`✅ Track ${trackIndex} ${shouldMute ? 'muted' : 'unmuted'} via ${audioElements.length} audio elements`);
            success = true;
          }
        }
      }
      
      if (!success) {
        console.error(`❌ Failed to ${shouldMute ? 'mute' : 'unmute'} track ${trackIndex} - no working method found`);
        console.log('Available methods:', {
          'playlist.tracks': !!playlist.tracks,
          'playlist.getInfo': !!playlist.getInfo,
          'playlist.ee': !!playlist.ee,
          'DOM channels': !!containerRef.current?.querySelectorAll('.channel')?.length
        });
      }
    } catch (error) {
      console.error('Error in muteTrack:', error);
    }
  };

  // Function to handle mute button click (simplified approach)
  const handleMute = (trackIndex) => {
    console.log(`🔇 Mute clicked for track ${trackIndex}`);
    
    if (!playlistRef.current) {
      console.error('No playlist available');
      return;
    }

    // Try to use the library's built-in mute functionality
    try {
      const playlist = playlistRef.current;
      if (playlist.ee && playlist.tracks && playlist.tracks[trackIndex]) {
        console.log('🔊 Using library mute event');
        playlist.ee.emit('mute', playlist.tracks[trackIndex]);
        return;
      }
    } catch (error) {
      console.warn('Library mute failed, using manual approach:', error);
    }

    // Fallback to manual state tracking
    setTrackStates(prev => {
      const currentlyMuted = Boolean(prev[`mute_${trackIndex}`]);
      const newStates = { ...prev };
      newStates[`mute_${trackIndex}`] = !currentlyMuted;
      
      console.log(`🔊 Track ${trackIndex} mute state: ${currentlyMuted} -> ${!currentlyMuted}`);
      
      // Apply the actual mute/unmute
      muteTrack(trackIndex, !currentlyMuted);
      
      // Update button appearances with new states
      if (containerRef.current) {
        console.log(`🎨 Calling updateButtonStates after mute change`);
        setTimeout(() => updateButtonStates(containerRef.current, newStates), 100);
      }
      
      // Update button states
      setTimeout(() => {
        updateButtonStates(containerRef.current, newStates);
      }, 50);
      
      return newStates;
    });
  };

  // Function to handle solo button click (simplified approach)
  const handleSolo = (trackIndex) => {
    console.log(`🎯 Solo clicked for track ${trackIndex}`);
    
    if (!playlistRef.current) {
      console.error('No playlist available');
      return;
    }

    // Try to use the library's built-in solo functionality
    try {
      const playlist = playlistRef.current;
      if (playlist.ee && playlist.tracks && playlist.tracks[trackIndex]) {
        console.log('🎵 Using library solo event');
        playlist.ee.emit('solo', playlist.tracks[trackIndex]);
        return;
      }
    } catch (error) {
      console.warn('Library solo failed, using manual approach:', error);
    }

    // Fallback to manual exclusive solo
    setTrackStates(prev => {
      const currentlySolo = Boolean(prev[`solo_${trackIndex}`]);
      const newStates = { ...prev };
      
      // Clear all solo states first (exclusive)
      for (let i = 0; i < (validatedTracks?.length || 0); i++) {
        newStates[`solo_${i}`] = false;
      }
      
      // Toggle: if wasn't solo, make it solo
      if (!currentlySolo) {
        newStates[`solo_${trackIndex}`] = true;
      }
      
      console.log(`🎵 Track ${trackIndex} solo state: ${currentlySolo} -> ${newStates[`solo_${trackIndex}`]}`);
      
      // Apply audio changes
      for (let i = 0; i < (validatedTracks?.length || 0); i++) {
        const shouldMute = newStates[`solo_${trackIndex}`] && i !== trackIndex;
        muteTrack(i, shouldMute);
      }
      
      // Update button states
      setTimeout(() => {
        updateButtonStates(containerRef.current, newStates);
      }, 50);
      
      return newStates;
    });
  };

  // Function to fix track alignment after loading
  const fixTrackAlignment = (container) => {
    if (!container) return;

    console.log('Fixing track alignment...');

    // Use the same controls width as configured in the playlist options.
    // The library positions the playhead based on this value, so we must
    // keep our layout in sync with it to avoid a left offset.
    const computedControlsWidth = 150;

    // Force timeline to align with controls dynamically
    const timescaleElements = container.querySelectorAll('.playlist-time-scale, .time-scale, .timescale');
    timescaleElements.forEach((element) => {
      element.style.marginLeft = `${computedControlsWidth}px`;
      element.style.paddingLeft = '0px';
      element.style.marginTop = '0px';
      element.style.marginBottom = '0px';
    });

    // Ensure each channel aligns properly and remove extra gaps
    const channels = container.querySelectorAll('.channel');
    channels.forEach((channel) => {
      channel.style.display = 'flex';
      channel.style.alignItems = 'stretch';
      channel.style.minHeight = '120px';
      channel.style.margin = '0';
      channel.style.padding = '0';
      channel.style.borderBottom = 'none';
    });

    // Ensure controls have consistent width and alignment
    const controls = container.querySelectorAll('.controls');
    controls.forEach((control) => {
      control.style.width = '150px';
      control.style.minWidth = '150px';
      control.style.maxWidth = '150px';
      control.style.flexShrink = '0';
      control.style.margin = '0';
      control.style.padding = '8px';
      control.style.boxSizing = 'border-box';
    });

    // Fix playlist container
    const playlist = container.querySelector('.playlist');
    if (playlist) {
      playlist.style.margin = '0';
      playlist.style.padding = '0';
    }

    console.log('Track alignment fixed');
  };

  // Function to set up mute/solo button event handlers
  const setupMuteSoloHandlers = (container) => {
    if (!container) return;
    
    console.log('Setting up mute/solo button handlers...');
    
    // Debug: log the playlist structure
    if (playlistRef.current) {
      console.log('Playlist object:', playlistRef.current);
      console.log('Playlist tracks:', playlistRef.current.tracks);
      if (playlistRef.current.getInfo) {
        console.log('Playlist getInfo:', playlistRef.current.getInfo());
      }
    }
    
    const buttons = container.querySelectorAll('button');
    buttons.forEach((button) => {
      const buttonText = button.textContent?.toLowerCase().trim() || '';
      const buttonTitle = button.title?.toLowerCase() || '';
      
      // Find which track this button belongs to
      const trackElement = button.closest('.channel');
      if (!trackElement) return;
      
      const allChannels = Array.from(container.querySelectorAll('.channel'));
      const trackIndex = allChannels.indexOf(trackElement);
      
      if (trackIndex === -1) return;
      
      // Remove existing event listeners by cloning the button
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      // Add our custom event handlers
      if (buttonText.includes('mute') || buttonText.includes('unmute') || buttonTitle.includes('mute')) {
        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log(`🔇 Mute/Unmute button clicked for track ${trackIndex}`);
          handleMute(trackIndex);
        });
        console.log(`Setup mute/unmute handler for track ${trackIndex}`);
      } else if (buttonText.includes('solo') || buttonText.includes('tutti') || buttonTitle.includes('solo')) {
        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log(`🎯 Solo/Tutti button clicked for track ${trackIndex}`);
          handleSolo(trackIndex);
        });
        console.log(`Setup solo/tutti handler for track ${trackIndex}`);
      }
    });
    
    // Initial button state update
    setTimeout(() => updateButtonStates(container), 100);
    console.log('Mute/solo handlers setup complete');
  };

  // Function to update waveform colors based on mute/solo states
  const updateWaveformBackgrounds = (container, currentStates = null) => {
    if (!container) {
      console.warn('🎨 updateWaveformBackgrounds: No container provided');
      return;
    }
    
    const states = currentStates || trackStates;
    console.log('🎨 updateWaveformBackgrounds called with states:', states);

    // Try multiple possible selectors for tracks/channels
    const possibleSelectors = [
      '.channel',
      '.playlist-track', 
      '.track',
      '.waveform-track',
      '[class*="track"]',
      '[class*="channel"]'
    ];

    let channels = [];
    for (const selector of possibleSelectors) {
      channels = container.querySelectorAll(selector);
      console.log(`🔍 Selector "${selector}" found ${channels.length} elements`);
      if (channels.length > 0) break;
    }

    if (channels.length === 0) {
      console.warn('🎨 No track/channel elements found with any selector');
      return;
    }

    // Check if any track is in solo mode
    const hasSoloTrack = Object.keys(states).some(key => key.startsWith('solo_') && states[key] === true);
    console.log(`🎨 Has solo track: ${hasSoloTrack}`);

    channels.forEach((channel, trackIndex) => {
      const isMuted = states[`mute_${trackIndex}`] === true;
      const isSolo = states[`solo_${trackIndex}`] === true;
      
      console.log(`🎨 Processing track ${trackIndex}: muted=${isMuted}, solo=${isSolo}`);
      
      let waveformColor;
      
      if (hasSoloTrack) {
        // If any track is solo, only solo tracks get the "active" color
        if (isSolo) {
          waveformColor = 'rgba(91, 13, 98, 0.3)'; // Dark purple with high transparency
          console.log(`🎨 Track ${trackIndex}: Solo - active waveform color (transparent dark purple)`);
        } else {
          waveformColor = 'rgba(243, 234, 243, 0.4)'; // Light purple with high transparency
          console.log(`🎨 Track ${trackIndex}: Not solo - muted waveform color (transparent light purple)`);
        }
      } else {
        // No solo tracks, use mute state
        if (isMuted) {
          waveformColor = 'rgba(243, 234, 243, 0.4)'; // Light purple with high transparency
          console.log(`🎨 Track ${trackIndex}: Muted - transparent light purple waveform color`);
        } else {
          waveformColor = 'rgba(91, 13, 98, 0.3)'; // Dark purple with high transparency
          console.log(`🎨 Track ${trackIndex}: Active - transparent dark purple waveform color`);
        }
      }
      
      // Find canvas elements within this channel for direct waveform coloring
      const canvases = channel.querySelectorAll('canvas');
      console.log(`🎨 Found ${canvases.length} canvases for track ${trackIndex}`);
      
      canvases.forEach((canvas, canvasIndex) => {
        try {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            // Store original image data if not already stored
            if (!canvas._originalImageData) {
              canvas._originalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            }
            
            // Restore original and apply color filter
            ctx.putImageData(canvas._originalImageData, 0, 0);
            ctx.globalCompositeOperation = 'source-atop';
            ctx.fillStyle = waveformColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'source-over';
            
            console.log(`🎨 Applied color ${waveformColor} to canvas ${canvasIndex} of track ${trackIndex}`);
          }
        } catch (error) {
          console.warn(`🎨 Failed to update canvas ${canvasIndex} for track ${trackIndex}:`, error);
        }
      });
    });
  };

  // Function to update button visual states
  const updateButtonStates = (container, currentStates = null) => {
    if (!container) {
      console.warn('🎨 updateButtonStates: No container provided');
      return;
    }
    
    // Use provided states or fall back to component state
    const states = currentStates || trackStates;
    console.log('🎨 updateButtonStates called with states:', states);
    
    // Update waveform backgrounds first
    updateWaveformBackgrounds(container, states);
    
    const buttons = container.querySelectorAll('button');
    console.log(`🎨 Found ${buttons.length} buttons to update`);
    
    // Debug: Log all buttons we find
    buttons.forEach((button, idx) => {
      console.log(`🔍 Button ${idx}:`, {
        text: button.textContent?.trim(),
        className: button.className,
        title: button.title,
        id: button.id,
        tagName: button.tagName,
        parentClass: button.parentElement?.className,
        hasTextMute: button.textContent?.toLowerCase().includes('mute'),
        hasTextSolo: button.textContent?.toLowerCase().includes('solo')
      });
    });
    
    // Also check if we can find ANY element with mute/solo text
    const allElements = container.querySelectorAll('*');
    let muteElements = 0, soloElements = 0;
    allElements.forEach(el => {
      const text = el.textContent?.toLowerCase() || '';
      if (text.includes('mute')) muteElements++;
      if (text.includes('solo')) soloElements++;
    });
    console.log(`🔍 Found ${muteElements} elements containing "mute", ${soloElements} containing "solo"`);
    
    // Try to find buttons by text content directly
    const buttonsByText = Array.from(buttons).filter(btn => {
      const text = btn.textContent?.toLowerCase() || '';
      return text.includes('mute') || text.includes('solo');
    });
    console.log(`🔍 Found ${buttonsByText.length} buttons with mute/solo text:`, 
      buttonsByText.map(btn => btn.textContent?.trim()));
    
    
    // Also try to find buttons by common waveform-playlist class names
    const muteButtons = container.querySelectorAll('.btn-mute, button[class*="mute"], button[title*="mute"], button[aria-label*="mute"]');
    const soloButtons = container.querySelectorAll('.btn-solo, button[class*="solo"], button[title*="solo"], button[aria-label*="solo"]');
    console.log(`🎨 Found ${muteButtons.length} potential mute buttons, ${soloButtons.length} potential solo buttons`);
    
    buttons.forEach((button) => {
      const buttonText = button.textContent?.toLowerCase().trim() || '';
      const buttonTitle = button.title?.toLowerCase() || '';
      
      const trackElement = button.closest('.channel');
      if (!trackElement) return;
      
      const allChannels = Array.from(container.querySelectorAll('.channel'));
      const trackIndex = allChannels.indexOf(trackElement);
      
      if (trackIndex === -1) return;
      
      console.log(`🎨 Processing button for track ${trackIndex}: "${buttonText}" (title: "${buttonTitle}")`);
      console.log(`🎨 Button classes:`, button.className);
      console.log(`🎨 Button full text content:`, JSON.stringify(button.textContent));
      
      // Debug current button state
      const currentMuteState = states[`mute_${trackIndex}`];
      const currentSoloState = states[`solo_${trackIndex}`];
      console.log(`🎨 Track ${trackIndex} states - mute: ${currentMuteState}, solo: ${currentSoloState}`);
    
      
      // Update mute button appearance and text
      if (buttonText.includes('mute') || buttonText.includes('unmute') || buttonTitle.includes('mute') || button.classList.contains('btn-mute')) {
        const isMuted = states[`mute_${trackIndex}`] === true;
        console.log(`🔇 Updating mute button for track ${trackIndex}: isMuted = ${isMuted}, current text = "${buttonText}"`);
        
        if (isMuted) {
          const oldText = button.textContent;
          button.textContent = 'unmute'; // Show "unmute" when currently muted
          console.log(`🔇 Changed mute button text from "${oldText}" to "unmute"`);
          button.style.backgroundColor = '#5b0d62'; // Dark purple for consistency
          button.style.color = 'white';
          button.style.opacity = '1';
          button.style.boxShadow = '0 0 8px rgba(91, 13, 98, 0.5)';
          button.style.transform = 'scale(1.02)';
        } else {
          const oldText = button.textContent;
          button.textContent = 'mute'; // Show "mute" when not muted
          console.log(`🔇 Changed mute button text from "${oldText}" to "mute"`);
          button.style.backgroundColor = '#f3eaf3'; // Light purple for inactive
          button.style.color = '#5b0d62';
          button.style.opacity = '0.8';
          button.style.boxShadow = 'none';
          button.style.transform = 'scale(1)';
        }
      }
      // Update solo button appearance and text
      else if (buttonText.includes('solo') || buttonText.includes('tutti') || buttonTitle.includes('solo') || button.classList.contains('btn-solo')) {
        const isSolo = states[`solo_${trackIndex}`] === true;
        console.log(`🎯 Updating solo button for track ${trackIndex}: isSolo = ${isSolo}, current text = "${buttonText}"`);
        
        if (isSolo) {
          const oldText = button.textContent;
          button.textContent = 'tutti'; // Show "tutti" when currently solo (clicking will unsolo)
          console.log(`🎯 Changed solo button text from "${oldText}" to "tutti"`);
          button.style.backgroundColor = '#5b0d62'; // Dark purple for consistency
          button.style.color = 'white';
          button.style.opacity = '1';
          button.style.boxShadow = '0 0 8px rgba(91, 13, 98, 0.5)';
          button.style.transform = 'scale(1.02)';
        } else {
          const oldText = button.textContent;
          button.textContent = 'solo'; // Show "solo" when not solo
          console.log(`🎯 Changed solo button text from "${oldText}" to "solo"`);
          button.style.backgroundColor = '#f3eaf3'; // Light purple for inactive
          button.style.color = '#5b0d62';
          button.style.opacity = '0.8';
          button.style.boxShadow = 'none';
          button.style.transform = 'scale(1)';
        }
      } else {
        // Debug: Show buttons that don't match our criteria
        console.log(`❌ Button for track ${trackIndex} doesn't match mute/solo criteria: "${buttonText}" (title: "${buttonTitle}", classes: ${button.className})`);
      }
    });
    
    // Fallback: Try to update buttons found by specific selectors
    console.log('🎨 Fallback: Trying direct selector approach...');
    
    // Update mute buttons directly
    muteButtons.forEach((button, index) => {
      const trackElement = button.closest('.channel');
      if (!trackElement) return;
      
      const allChannels = Array.from(container.querySelectorAll('.channel'));
      const trackIndex = allChannels.indexOf(trackElement);
      
      if (trackIndex >= 0) {
        const isMuted = states[`mute_${trackIndex}`] === true;
        console.log(`🔇 Direct mute button update for track ${trackIndex}: isMuted = ${isMuted}`);
        
        if (isMuted) {
          button.textContent = 'unmute';
          button.style.backgroundColor = '#5b0d62'; // Dark purple for consistency
          button.style.color = 'white';
          button.style.opacity = '1';
          button.style.boxShadow = '0 0 8px rgba(91, 13, 98, 0.5)';
          button.style.transform = 'scale(1.02)';
        } else {
          button.textContent = 'mute';
          button.style.backgroundColor = '#f3eaf3'; // Light purple for inactive
          button.style.color = '#5b0d62';
          button.style.opacity = '0.8';
          button.style.boxShadow = 'none';
          button.style.transform = 'scale(1)';
        }
      }
    });
    
    // Update solo buttons directly
    soloButtons.forEach((button, index) => {
      const trackElement = button.closest('.channel');
      if (!trackElement) return;
      
      const allChannels = Array.from(container.querySelectorAll('.channel'));
      const trackIndex = allChannels.indexOf(trackElement);
      
      if (trackIndex >= 0) {
        const isSolo = states[`solo_${trackIndex}`] === true;
        console.log(`🎯 Direct solo button update for track ${trackIndex}: isSolo = ${isSolo}`);
        
        if (isSolo) {
          button.textContent = 'tutti';
          button.style.backgroundColor = '#5b0d62'; // Dark purple for consistency
          button.style.color = 'white';
          button.style.opacity = '1';
          button.style.boxShadow = '0 0 8px rgba(91, 13, 98, 0.5)';
          button.style.transform = 'scale(1.02)';
        } else {
          button.textContent = 'solo';
          button.style.backgroundColor = '#f3eaf3'; // Light purple for inactive
          button.style.color = '#5b0d62';
          button.style.opacity = '0.8';
          button.style.boxShadow = 'none';
          button.style.transform = 'scale(1)';
        }
      }
    });
  };

  // Function to apply fixed purple styling to ALL buttons under player tracks
  const applyFixedButtonStyling = (container) => {
    if (!container) return;
    
    console.log('🎨 Applying fixed purple styling to ALL buttons...');
    
    // Try multiple selectors to find buttons
    const selectors = [
      'button',
      '[role="button"]',
      '.btn',
      '.button',
      'input[type="button"]',
      '*[class*="btn"]',
      '*[class*="button"]'
    ];
    
    let allButtons = [];
    selectors.forEach(selector => {
      const buttons = container.querySelectorAll(selector);
      console.log(`🔍 Selector "${selector}" found ${buttons.length} elements`);
      allButtons = [...allButtons, ...Array.from(buttons)];
    });
    
    // Remove duplicates
    allButtons = [...new Set(allButtons)];
    console.log(`🎨 Found ${allButtons.length} unique buttons - styling ALL`);
    
    allButtons.forEach((button, idx) => {
      const buttonText = button.textContent?.toLowerCase().trim() || '';
      const buttonClass = button.className || '';
      
      // Skip play/pause/stop buttons (our custom media controls)
      if (buttonClass.includes('control-btn') || 
          buttonClass.includes('play-pause-btn') || 
          buttonClass.includes('stop-btn')) {
        console.log(`⏭️ Skipping media control button ${idx}: "${buttonText}" (class: ${buttonClass})`);
        return;
      }
      
      // Only style actual button elements, not divs or other containers
      if (button.tagName.toLowerCase() !== 'button') {
        console.log(`⏭️ Skipping non-button element ${idx}: ${button.tagName} "${buttonText}"`);
        return;
      }
      
      // Only style if it contains mute/solo text (to avoid styling container elements)
      if (!buttonText.includes('mute') && !buttonText.includes('solo') && !buttonText.includes('tutti')) {
        console.log(`⏭️ Skipping button without mute/solo text ${idx}: "${buttonText}"`);
        return;
      }
      
      console.log(`🎨 Styling individual button ${idx}: "${buttonText}" (tag: ${button.tagName}, class: ${button.className})`);
      
      // Apply clean purple styling to individual buttons only with !important
      button.style.setProperty('background-color', '#f3eaf3', 'important');
      button.style.setProperty('color', '#5b0d62', 'important');
      button.style.setProperty('border', 'none', 'important');
      button.style.setProperty('border-radius', '6px', 'important');
      button.style.setProperty('padding', '6px 12px', 'important');
      button.style.setProperty('font-size', '11px', 'important');
      button.style.setProperty('font-weight', '500', 'important');
      button.style.setProperty('cursor', 'pointer', 'important');
      button.style.setProperty('transition', 'all 0.2s ease', 'important');
      button.style.setProperty('margin', '2px', 'important');
      button.style.setProperty('min-width', '45px', 'important');
      button.style.setProperty('text-align', 'center', 'important');
      button.style.setProperty('display', 'inline-block', 'important');
      
      // Remove existing event listeners and add new ones
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      // Hover effect for individual buttons only
      newButton.addEventListener('mouseenter', () => {
        newButton.style.setProperty('background-color', '#5b0d62', 'important');
        newButton.style.setProperty('color', 'white', 'important');
        newButton.style.setProperty('transform', 'scale(1.05)', 'important');
      });
      newButton.addEventListener('mouseleave', () => {
        newButton.style.setProperty('background-color', '#f3eaf3', 'important');
        newButton.style.setProperty('color', '#5b0d62', 'important');
        newButton.style.setProperty('transform', 'scale(1)', 'important');
      });
    });
    
    console.log('🎨 Fixed purple styling applied to ALL buttons');
  };

  // Function to remove unwanted buttons (remove/collapse) and optionally solo/mute for single tracks
  const removeUnwantedButtons = (container) => {
    if (!container) return;

    // For single-track players (like Full Mix), hide solo/mute buttons as they don't make sense
    const isSingleTrack = tracks && tracks.length === 1;

    // Target remove and collapse buttons, and optionally solo/mute for single tracks
    const unwantedSelectors = [
      'button[title*="Remove"]',
      'button[title*="remove"]', 
      'button[title*="Collapse"]',
      'button[title*="collapse"]',
      'button[aria-label*="Remove"]',
      'button[aria-label*="remove"]',
      'button[aria-label*="Collapse"]',
      'button[aria-label*="collapse"]',
      '.btn.remove',
      '.btn.collapse',
      '.btn-remove',
      '.btn-collapse',
      '.remove-track',
      '.collapse-track'
    ];

    // Add solo/mute buttons to unwanted list for single-track players
    if (isSingleTrack) {
      unwantedSelectors.push(
        'button[title*="Mute"]',
        'button[title*="mute"]',
        'button[title*="Solo"]', 
        'button[title*="solo"]',
        'button[aria-label*="Mute"]',
        'button[aria-label*="mute"]',
        'button[aria-label*="Solo"]',
        'button[aria-label*="solo"]',
        '.btn-mute',
        '.btn-solo'
      );
      console.log('Hiding solo/mute buttons for single-track player');
    }

    unwantedSelectors.forEach(selector => {
      const elements = container.querySelectorAll(selector);
      elements.forEach(element => {
        // For single-track players, remove solo/mute buttons too
        // For multi-track players, preserve solo/mute buttons
        const text = element.textContent?.toLowerCase() || '';
        const title = element.title?.toLowerCase() || '';
        const ariaLabel = element.getAttribute('aria-label')?.toLowerCase() || '';
        
        const isSoloMuteButton = text.includes('solo') || text.includes('mute') || text.includes('tutti') || text.includes('unmute') ||
                                title.includes('solo') || title.includes('mute') ||
                                ariaLabel.includes('solo') || ariaLabel.includes('mute');
        
        if (isSingleTrack || !isSoloMuteButton) {
          element.remove();
          if (isSingleTrack && isSoloMuteButton) {
            console.log(`Removed ${text || title || 'solo/mute'} button for single-track player`);
          }
        }
      });
    });
  };


  // Reset all solo/mute states after Stop
  const resetAllMuteSoloStates = () => {
    const playlist = playlistRef.current;
    if (!playlist) return;

    try {
      // 1) Hard reset library's internal mute/solo arrays
      if (Array.isArray(playlist.soloedTracks) && playlist.soloedTracks.length > 0) {
        playlist.soloedTracks = [];
      }
      if (Array.isArray(playlist.mutedTracks) && playlist.mutedTracks.length > 0) {
        playlist.mutedTracks = [];
      }

      // 2) Recompute playout and redraw
      if (typeof playlist.adjustTrackPlayout === 'function') {
        playlist.adjustTrackPlayout();
      }
      if (typeof playlist.draw === 'function' && typeof playlist.render === 'function') {
        playlist.draw(playlist.render());
      } else if (playlist.ee) {
        // Fallback to trigger a draw via statechange
        playlist.ee.emit('statechange', 'cursor');
      }

      // 3) Reset local UI state and refresh button styles
      setTrackStates({});
      setTimeout(() => updateButtonStates(containerRef.current, {}), 50);
    } catch (err) {
      console.warn('Failed to reset solo/mute states after stop:', err);
    }
  };

  useEffect(() => {
    // Catch global unhandled decode errors and gracefully fallback
    const handler = (event) => {
      const msg = String(event?.reason?.message || event?.reason || '').toLowerCase();
      if (msg.includes('unable to decode audio data') || (msg.includes('decode') && msg.includes('audio'))) {
        console.warn('Captured unhandled audio decode error; switching to fallback players. Reason:', event?.reason);
        try {
          if (playlistRef.current) {
            if (playlistRef.current.ee) {
              playlistRef.current.ee.off('mute');
              playlistRef.current.ee.off('solo');
              playlistRef.current.ee.off('volumechange');
            }
            playlistRef.current.clear?.();
          }
        } catch (_) {}
        setLoadError('Unable to decode audio data');
        setIsLoading(false);
      }
    };
    window.addEventListener('unhandledrejection', handler);
    unhandledRejectionHandlerRef.current = handler;
    return () => {
      const h = unhandledRejectionHandlerRef.current;
      if (h) window.removeEventListener('unhandledrejection', h);
    };
  }, []);

  useEffect(() => {
    console.log(`MultitrackPlayer useEffect triggered for ${modelConfig.displayName}`, { 
      hasContainer: !!containerRef.current,
      tracksLength: tracks?.length,
      exampleId: selectedExample?.id
    });

    if (!containerRef.current || !tracks || tracks.length === 0) {
      console.log('MultitrackPlayer: Early return - missing requirements');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    setTrackStates({}); // Reset track states for new tracks

    // More thorough cleanup of previous playlist
    if (playlistRef.current) {
      try {
        playlistRef.current.clear();
        playlistRef.current = null;
      } catch (error) {
        console.warn('Error clearing previous playlist:', error);
      }
    }
    
    // Completely clear container
    containerRef.current.innerHTML = '';

    // Add a small delay to ensure cleanup is complete
    const timeoutId = setTimeout(() => {
      try {
        // Initialize waveform playlist using the correct syntax for v4.3.3
        const playlist = WaveformPlaylist({
          samplesPerPixel: 1024,
          waveHeight: 100, // Increased height for better visibility
          container: containerRef.current,
          state: 'cursor',
          colors: {
            waveOutlineColor: '#E0E7FF',
            waveFillColor: '#8B5CF6',
            timeColor: '#666',
            playheadColor: '#ff0000',   // Red playhead
            playheadFillColor: '#ff0000'
          },
          controls: {
            show: true,
            width: 150
          },
          timescale: true,
          zoomLevels: [500, 1000, 1024, 3000, 5000],
          isAutomaticScroll: false,
          seekStyle: 'line',
          exclSolo: true,              // Enable exclusive solo behavior (like Audacity)
          options: {
            removeTrack: false,        // Hide remove track button
            changeTrackView: false     // Hide collapse track button
          }
        });

        playlistRef.current = playlist;

        // Set up event listeners to sync UI with library state changes
        if (playlist.ee) {
          const getTrackIndex = (t) => {
            const playlistObj = playlistRef.current;
            if (typeof t === 'number') return t;
            if (playlistObj && Array.isArray(playlistObj.tracks)) {
              const idx = playlistObj.tracks.findIndex((x) => x === t);
              if (idx !== -1) return idx;
            }
            if (t && typeof t.id === 'number') return t.id;
            return 0;
          };

          // Listen for library mute/solo events and update our button states
          
          // Listen for mute toggle events
          playlist.ee.on('mute', (track) => {
            const index = getTrackIndex(track);
            console.log('📻 Library mute event for track:', index);
            setTrackStates(prev => {
              const newStates = { ...prev };
              newStates[`mute_${index}`] = !Boolean(prev[`mute_${index}`]);
              console.log(`📻 Library mute: Track ${index} -> ${newStates[`mute_${index}`]}`);
              setTimeout(() => updateButtonStates(containerRef.current, newStates), 50);
              return newStates;
            });
          });

          // Listen for solo toggle events  
          playlist.ee.on('solo', (track) => {
            const index = getTrackIndex(track);
            console.log("📻 Library solo event for track:", index);
            setTrackStates(prev => {
              const wasSolo = Boolean(prev[`solo_${index}`]);
              const newStates = { ...prev };
              // Clear all solo states first (exclusive solo)
              tracks.forEach((_, i) => {
                newStates[`solo_${i}`] = false;
              });
              // Toggle the clicked track
              if (!wasSolo) {
                newStates[`solo_${index}`] = true;
              }
              console.log(`📻 Library solo: Track ${index} -> ${newStates[`solo_${index}`]}`);
              setTimeout(() => updateButtonStates(containerRef.current, newStates), 50);
              return newStates;
            });
          });

          // Listen for volume changes from the library
          playlist.ee.on('volumechange', (volume, track) => {
            const index = getTrackIndex(track);
            console.log(`Library changed volume of track ${index} to ${volume}`);
            setTrackStates(prev => {
              const newStates = { ...prev, [`mute_${index}`]: (volume === 0) };
              setTimeout(() => updateButtonStates(containerRef.current, newStates), 50);
              return newStates;
            });
          });
        }

        // Prepare tracks for loading with default settings
        const tracksToLoad = tracks.map((track) => ({
          src: track.src,
          name: `${track.icon} ${track.name}`,
          gain: 1.0      // Default volume (100%)
        }));

        // Pre-validate that audio URLs are reachable to reduce decode errors from 404s
        const checkUrl = async (url) => {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            const res = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: controller.signal });
            clearTimeout(timeoutId);
            if (!res.ok) return false;
            const contentType = res.headers.get('content-type') || '';
            const length = Number(res.headers.get('content-length') || '1');
            // Accept common audio types or unknown type with non-zero length
            return (/(audio\/|application\/octet-stream)/i.test(contentType) && length > 0) || length > 0;
          } catch (e) {
            console.warn('HEAD check failed, will try to load anyway:', url, e?.name || e?.message || e);
            // If HEAD is blocked, optimistically allow load to proceed
            return true;
          }
        };

        const validateTracks = async () => {
          const results = await Promise.all(tracksToLoad.map(async (t) => ({ t, ok: await checkUrl(t.src) })));
          const okTracks = results.filter(r => r.ok).map(r => r.t);
          const badTracks = results.filter(r => !r.ok).map(r => r.t.src);
          if (badTracks.length > 0) {
            console.warn('Some track URLs appear unavailable and will be skipped:', badTracks);
          }
          setValidatedTracks(okTracks.length > 0 ? okTracks : tracksToLoad);
          return okTracks.length > 0 ? okTracks : tracksToLoad; // fallback to original list if all failed checks
        };

        validateTracks().then(async (readyTracks) => {
          console.log(`Loading ${readyTracks.length} tracks for ${modelConfig.displayName || 'Player'}:`, readyTracks.map(t => t.name));

          // Load tracks one-by-one to tolerate occasional decode failures
          let loadedCount = 0;
          for (const track of readyTracks) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await playlist.load([track]);
              loadedCount += 1;
            } catch (e) {
              console.warn('Skipping track due to decode/load error:', track.src, e?.message || e);
            }
          }

          if (loadedCount === 0) {
            throw new Error('No audio tracks could be loaded');
          }
        }).then(() => {
          console.log('Successfully loaded at least one track');
          
          // Fix alignment, setup handlers, apply styling, and remove unwanted buttons after loading
          setTimeout(() => {
            fixTrackAlignment(containerRef.current);
            removeUnwantedButtons(containerRef.current);
            applyFixedButtonStyling(containerRef.current); // Apply purple styling
            setupMuteSoloHandlers(containerRef.current);
            
            // Try styling again with more delays in case buttons load later
            setTimeout(() => applyFixedButtonStyling(containerRef.current), 500);
            setTimeout(() => applyFixedButtonStyling(containerRef.current), 1000);
            setTimeout(() => applyFixedButtonStyling(containerRef.current), 2000);
            
            setIsLoading(false); // Set loading to false after all setup is complete
          }, 300);
        }).catch(error => {
          console.error('Error loading tracks in playlist:', error);
          setLoadError(error?.message || 'Failed to load audio tracks in playlist');
          setIsLoading(false);
        });

      } catch (error) {
        console.error('Error initializing playlist:', error);
        setLoadError(error.message || 'Failed to initialize audio player');
        setIsLoading(false);
      }
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (playlistRef.current) {
        try {
          // Remove event listeners
          if (playlistRef.current.ee) {
            playlistRef.current.ee.off('mute');
            playlistRef.current.ee.off('solo');
            playlistRef.current.ee.off('volumechange');
          }
          playlistRef.current.clear();
          playlistRef.current = null;
        } catch (error) {
          console.warn('Error cleaning up playlist:', error);
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExample.id, modelConfig.id, tracks]); // Re-run when tracks change

  return (
    <div className="multitrack-player">
      {!hideHeader && (
        <div className="multitrack-header">
          <div className="model-name">
            {/* Table 1 name display */}
            {!modelConfig.workflow && (
              <>
                {modelConfig.displayName}
                {modelConfig.displayName === 'C-(ii)' && <span className="best-indicator">(Our Best)</span>}
                {modelConfig.displayName === 'A-(i)' && <span className="best-indicator">(Prior Art)</span>}
              </>
            )}
            {/* Table 2 name display - same structure as basic player */}
            {modelConfig.workflow && (
              <>
                {modelConfig.name}
                {modelConfig.name === 'C-(ii)' && modelConfig.description === '2 Pass' && <span className="best-indicator">(Our Best)</span>}
                {modelConfig.name === 'A-(i)' && <span className="best-indicator">(Prior Art)</span>}
              </>
            )}
          </div>
          {/* Table 2: Use same structure as basic player */}
          {modelConfig.workflow ? (
            <>
              <div className="model-description">{modelConfig.description}</div>
              {typeof generationTime === 'number' && (
                <div className="generation-time">
                  <span className="time-display">
                    Inference ⏱️  &nbsp; {generationTime.toFixed(1)}s
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="model-settings">
              <span className="setting-item">
                <span className="setting-label">data group:</span>
                <span className={`setting-value ${modelConfig.id.startsWith('model_A') ? 'disabled' : 'enabled'}`}>
                  {modelConfig.id.startsWith('model_A') ? '✗' : '✓'}
                </span>
              </span>
              <span className="setting-item">
                <span className="setting-label">noise share (train):</span>
                <span className={`setting-value ${modelConfig.id === 'model_C_ii' ? 'enabled' : 'disabled'}`}>
                  {modelConfig.id === 'model_C_ii' ? '✓' : '✗'}
                </span>
              </span>
              <span className="setting-item">
                <span className="setting-label">noise share (infer):</span>
                <span className={`setting-value ${(modelConfig.id === 'model_A_ii' || modelConfig.id === 'model_B_ii' || modelConfig.id === 'model_C_ii') ? 'enabled' : 'disabled'}`}>
                  {(modelConfig.id === 'model_A_ii' || modelConfig.id === 'model_B_ii' || modelConfig.id === 'model_C_ii') ? '✓' : '✗'}
                </span>
              </span>
              {typeof generationTime === 'number' && (
                <span className="setting-item">
                  <span className="setting-label">
                    Inference ⏱️  &nbsp; {generationTime.toFixed(1)}s
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Custom Controls */}
      <div className="multitrack-controls">
        <button 
          className={`control-btn play-pause-btn ${isPlaying ? 'playing' : 'paused'}`} 
          onClick={handlePlayPauseToggle} 
          disabled={isLoading} 
          title={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" fill="currentColor"/>
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
            </svg>
          )}
        </button>
        <button className="control-btn stop-btn" onClick={handleStop} disabled={isLoading} title="Stop">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <rect x="6" y="6" width="12" height="12" fill="currentColor"/>
          </svg>
        </button>
        {isLoading && <span className="loading-indicator">Loading audio...</span>}
        {loadError && <span className="error-indicator">Error: {loadError}</span>}
      </div>

      <div className="waveform-container" ref={containerRef}></div>
      
      {/* Fallback: Simple audio players if waveform fails */}
      {loadError && (
        <div className="fallback-audio-players">
          <div className="fallback-header">Fallback Audio Players:</div>
          {tracks.map((track, index) => (
            <div key={index} className="fallback-audio-row">
              <span className="fallback-track-name">{track.icon} {track.name}</span>
              <audio 
                controls 
                preload="none" 
                className="fallback-audio-control"
                src={track.src}
                onError={(e) => console.warn('Fallback audio failed to load', track.name, e?.target?.src)}
              >
                Your browser does not support audio playback.
              </audio>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultitrackPlayer;
